/** Prefer vendor_market_id-style keys for Bookmaker-API → Result-API settle. */
export type ResultMarketIds = {
  betfairId?: string
  id?: string
  _id: string
}

/** Runner fields used when declaring a Bookmaker / horse-racing result. */
export type ResultRunnerIds = {
  selectionId?: string
  selection_id?: string
  id?: string
  _id?: string
  name?: string
  runnerName?: string
}

export type SettledMarketFlags = {
  status?: string | null
  isResult?: boolean
  result?: string | null
  winner?: string | null
  winnerName?: string | null
}

/**
 * Market id sent on result declare: vendor market id first, then catalog id, then `_id`.
 */
export function resultMarketId(market: ResultMarketIds): string {
  return String(market.betfairId || market.id || market._id || '').trim()
}

/**
 * Horse / selection id for a winner list entry: selectionId → id → `_id`.
 */
export function resultHorseSelectionId(horse: ResultRunnerIds): string {
  return String(horse.selectionId ?? horse.id ?? horse._id ?? '').trim()
}

/**
 * Resolve the winning runner from the market list, then prefer selectionId → id → runnerId.
 * Returns null when no runnerId was provided (tie / abandon).
 */
export function resultWinnerHorseId(
  runners: ResultRunnerIds[],
  runnerId: string | null | undefined,
): string | null {
  const rid = String(runnerId ?? '').trim()
  if (!rid) return null
  const runner = runners.find(
    (r) =>
      String(r._id ?? '') === rid ||
      String(r.id ?? '') === rid ||
      String(r.selectionId ?? '') === rid,
  )
  if (!runner) return rid
  return String(runner.selectionId || runner.id || rid).trim() || rid
}

/** Catalog status after Result-API settle is SETTLED (never treat CLOSED as settled UI state). */
export function isMarketSettled(market: SettledMarketFlags): boolean {
  return Boolean(market.isResult) || String(market.status ?? '').toUpperCase() === 'SETTLED'
}

export function isMarketTied(market: SettledMarketFlags): boolean {
  const status = String(market.status ?? '').toUpperCase()
  const result = String(market.result ?? '').toUpperCase()
  return status === 'TIED' || result === 'TIED' || result === 'VOID'
}

export function isMarketAbandoned(market: SettledMarketFlags): boolean {
  const status = String(market.status ?? '').toUpperCase()
  const result = String(market.result ?? '').toUpperCase()
  return status === 'ABANDONED' || result === 'ABANDONED'
}

/** Same lock as MarketsViewPage — settled/tied/abandoned cannot change enable status. */
export function isMarketStatusLocked(market: SettledMarketFlags): boolean {
  return isMarketSettled(market) || isMarketTied(market) || isMarketAbandoned(market)
}

/** Market Selection status column: locked markets show terminal label, else Enable/Disable. */
export function marketStatusLabel(market: SettledMarketFlags): string {
  if (isMarketAbandoned(market)) return 'ABANDONED'
  if (isMarketTied(market)) return 'TIED'
  if (isMarketSettled(market)) return 'SETTLED'
  return String(market.status ?? 'Enable')
}

/** Winner banner: settled (isResult / SETTLED) and not tie/abandon. */
export function shouldShowWinnerBanner(market: SettledMarketFlags): boolean {
  if (!isMarketSettled(market)) return false
  if (isMarketTied(market) || isMarketAbandoned(market)) return false
  return true
}

/** Resolve winner display name from runners by winner selection / id. */
export function winnerNameFromRunners(
  runners: ResultRunnerIds[],
  winnerId: string | null | undefined,
): string {
  const wid = String(winnerId ?? '').trim()
  if (!wid) return ''
  const runner = runners.find((r) => {
    const ids = [r.selectionId, r.selection_id, r.id, r._id]
      .map((x) => String(x ?? '').trim())
      .filter(Boolean)
    return ids.includes(wid)
  })
  return String(runner?.name ?? runner?.runnerName ?? '').trim()
}

/** Banner label: metadata winnerName first, then runner lookup. */
export function winnerBannerLabel(
  market: SettledMarketFlags & { runners?: ResultRunnerIds[] },
  resolvedName?: string | null,
): string {
  const fromMeta = String(market.winnerName ?? '').trim()
  if (fromMeta) return fromMeta
  const fromState = String(resolvedName ?? '').trim()
  if (fromState) return fromState
  return winnerNameFromRunners(market.runners ?? [], market.winner)
}
